import type NumberUnit from '@exodus/currency';
import Button from '@passkeys/ui/button';
import CopyableAddress from '@passkeys/ui/copyable-address';
import Icon from '@passkeys/ui/icon';
import Text from '@passkeys/ui/text';
import { useMemo } from 'react';

import type { Asset } from '@/types';
import actions from '@/ui/flux/actions';
import type { Nft } from '@/ui/types';
import { openInBlankWindow } from '@/ui/utils/navigation.js';
import Hero from './components/hero.js';

type SendSuccessModalProps = {
  amount?: NumberUnit;
  collectible?: Nft;
  asset: Asset;
  toAddress: string;
  txId: string;
  onClose?: () => void;
};

function SendSuccessModal({
  asset,
  amount,
  collectible,
  toAddress,
  txId,
  onClose,
}: SendSuccessModalProps) {
  const amountWithTicker = useMemo(() => {
    if (!amount) {
      return;
    }

    return `${amount.toDefaultString({ unit: false })} ${asset.displayTicker}`;
  }, [amount, asset.displayTicker]);

  const handleViewTransaction = async () => {
    const url = await asset.blockExplorer.txUrl(txId);
    openInBlankWindow(url);
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <div className="flex h-full flex-col items-center gap-12 pb-8">
      <Hero asset={amount && asset} collectible={collectible} />

      <div className="flex w-full flex-1 flex-col items-center">
        <Text size={24} align="center" className="leading-none">
          {amount && `${asset.displayTicker} sent`}
          {collectible && 'NFT sent'}
        </Text>

        <Text
          align="center"
          className="mb-3 mt-8 text-black/55 theme-dark:text-white/55"
        >
          {amount && `You sent ${amountWithTicker} to`}
          {collectible && 'Recipient address'}
        </Text>

        <CopyableAddress
          address={toAddress}
          chain={asset.baseAsset.name}
          chainDisplayName={asset.baseAsset.displayName}
          className="mx-6"
        />
      </div>

      <footer className="flex w-full flex-col items-center px-4">
        <Button
          className="w-full max-w-[296px]"
          dataTestId="send-success-confirm-button"
          onClick={handleClose}
          size="large"
        >
          OK
        </Button>

        <Button
          className="-mb-2 mt-2"
          onClick={handleViewTransaction}
          suffix={<Icon name="arrow-up-right" size={12} />}
          variant="tertiary"
          dataTestId="view-transaction-button"
        >
          View transaction
        </Button>
      </footer>
    </div>
  );
}

type TokenShowSendSuccessModalParams = {
  amount: NumberUnit;
  collectible?: never;
};

type CollectibleShowSendSuccessModalParams = {
  collectible: Nft;
  amount?: never;
};

type ShowSendSuccessModalParams = (
  | TokenShowSendSuccessModalParams
  | CollectibleShowSendSuccessModalParams
) &
  Omit<SendSuccessModalProps, 'amount' | 'collectible' | 'onClose'>;

function showSendSuccessModal({
  amount,
  collectible,
  asset,
  toAddress,
  txId,
}: ShowSendSuccessModalParams) {
  return actions.modal.show({
    title: 'Success',
    fullscreen: true,
    reactContent: (
      <SendSuccessModal
        amount={amount}
        collectible={collectible}
        asset={asset}
        toAddress={toAddress}
        txId={txId}
      />
    ),
  });
}

export default showSendSuccessModal;
